import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell, Button } from '@dj/ui';
import { callable } from '@dj/firebase-client';
import { useClaim } from '../state/ClaimContext';

interface LinkInfo {
  valid: boolean;
  reason?: string;
  providerName?: string;
  monthlyPriceCents?: number;
  subsidyPercent?: number;
  patientPortionCents?: number;
  firstName?: string;
  email?: string;
}

/**
 * /claim?t=… — opened from the emailed link (Tess re-scan 3, R11). Only a
 * valid, unused, unexpired link shows the provider and price and lets the
 * member go on to claim.
 */
export function ClaimLinkPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setFromLink } = useClaim();
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = params.get('t') || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const get = callable<{ token: string }, LinkInfo>('getClaimLink');
        const r = await get({ token });
        if (!cancelled) setInfo(r);
      } catch {
        if (!cancelled) setInfo({ valid: false, reason: 'error' });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (!info) {
    return (
      <AppShell title="Checking your link">
        <div className="bg-white rounded-lg shadow p-6 sm:p-8 max-w-lg mx-auto text-slate-700">Checking your link…</div>
      </AppShell>
    );
  }

  if (!info.valid) {
    return (
      <AppShell title="Link not valid">
        <div className="bg-white rounded-lg shadow p-6 sm:p-8 max-w-lg mx-auto">
          <h1 className="text-2xl font-semibold text-slate-900 mb-3">This link can't be used</h1>
          <p className="text-slate-700 mb-6">
            {info.reason === 'rate_limited'
              ? 'Too many attempts — please wait a while and try again.'
              : "It may have expired (links last 30 minutes), already been used, or the place has been claimed. You can ask for a new one."}
          </p>
          <Button variant="primary" onClick={() => navigate('/', { replace: true })}>Start again</Button>
        </div>
      </AppShell>
    );
  }

  const go = () => {
    if (!acceptedTerms) { setError('Please accept the terms to continue.'); return; }
    setFromLink(
      token,
      {
        providerId: '',
        providerName: info.providerName || '',
        monthlyPriceCents: info.monthlyPriceCents ?? 0,
        patientPortionCents: info.patientPortionCents ?? 0,
        subsidyPercent: info.subsidyPercent ?? 0,
        currency: 'NZD',
      },
      { firstName: info.firstName || '', email: info.email || '', acceptedTermsAt: new Date().toISOString() },
    );
    navigate('/payment');
  };

  return (
    <AppShell title="Finish setting up">
      <div className="bg-white rounded-lg shadow p-6 sm:p-8 max-w-lg mx-auto">
        <h1 className="text-2xl font-semibold text-slate-900 mb-3">
          Kia ora{info.firstName ? ` ${info.firstName}` : ''}
        </h1>
        <p className="text-slate-700 mb-4">
          <span className="font-semibold">{info.providerName}</span> is offering you Doctor's Journal Premium.
        </p>
        <p className="text-slate-700 mb-6">
          Your subscription will be linked to <span className="font-semibold">{info.email}</span>. Sign in to the
          app with this email address.
        </p>
        <label className="flex items-start gap-3 mb-5 cursor-pointer">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            className="mt-1 h-5 w-5 accent-teal-700"
          />
          <span className="text-sm text-slate-700">
            I agree to the{' '}
            <a href="https://brucysnz.github.io/doctorsjournal-legal/terms.html" target="_blank" rel="noopener noreferrer" className="text-teal-700 underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="https://brucysnz.github.io/doctorsjournal-legal/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="text-teal-700 underline">
              Privacy Policy
            </a>
            .
          </span>
        </label>
        {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded mb-3">{error}</div>}
        <Button variant="primary" fullWidth onClick={go}>Continue</Button>
      </div>
    </AppShell>
  );
}
